import { IChange } from 'json-diff-ts';

import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import EntityProperty from '@/common/models/EntityProperty';
import Exception from '@/common/models/Exception';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { isString } from '@/globals';

export interface IComplexValidationRule {
  rule: string;
  formName: string;
}

export interface IExtraValidation {
  message?: string;
  preEvaluated?: boolean;
  value?;
  rules?: string;
}

export interface IEntityChange<T> {
  property: EntityProperty<T>;
  updatedObject: T;
  change: {
    from: string;
    to: string;
    diff?: IChange[];
  };
}

export interface IGetEntityChanges<T> {
  getChanges(): Promise<IEntityChange<T>[]>;
}

/**
 * The keys used by tanstack to cache the queries for this entity.
 */
export interface IQueryKeys {
  /**
   * key for the getAll query
   */
  getAll: string;
}

class Validatable<T = unknown, identityType = number> {
  id: identityType;

  changes: IEntityChange<T>[];

  extraValidation: Record<string, IExtraValidation>;

  rawValues?: Record<string, never>; // Raw values from the spreadsheet when using Bulk V4. Object with similar properties.

  static entityPropertiesCached?: Record<string, EntityProperty<any>>;

  /**
   * The keys used by tanstack to cache the queries for this entity.
   */
  public static queryKeys: IQueryKeys;

  constructor() {
    this.changes = []; // Used to highlighted changes in tables.
    this.extraValidation = {}; // Used to display errors for particular cells in tables.
  }

  get isNew(): boolean {
    return this.id === -1;
  }

  /**
   * @returns {Record<string, string> | {}}
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static validatorCreateRules(model: Validatable<any>): Record<string, string | IComplexValidationRule> {
    return {};
  }

  static validatorUpdateRules(): Record<string, string> {
    return {};
  }

  static get entityProperties(): Record<string, EntityProperty<any>> {
    // Dictionary of EntityProperty
    throw new Exception('Error: Did not implement `entityProperties` getter');
  }

  static get supportsComplexBulkEdit() {
    // Override to allow complex bulk editing.
    return false;
  }

  static get bulkOperations() {
    return [];
  }

  static get entityName() {
    return 'Unknown';
  }

  static get bulkEditProperties() {
    return null;
  }

  static get bulkV4Properties() {
    return null;
  }

  static get bulkEditPropertiesAsAnObject() {
    if (!this.bulkEditProperties) return null;

    const entries = Object.entries(this.entityProperties);
    return this.bulkEditProperties.reduce((mem, x) => {
      const modified = { ...mem };
      const entry = entries.find((e) => JSON.stringify(e[1]) === JSON.stringify(x));
      if (entry) {
        modified[entry[0]] = x;
      } else {
        console.log('bulkEditPropertiesAsAnObject prop not found:', x);
      }
      return modified;
    }, {});
  }

  static get bulkV4PropertiesAsAnObject() {
    if (!this.bulkV4Properties) return null;

    const entries = Object.entries(this.entityProperties);
    return this.bulkV4Properties.reduce((mem, x) => {
      const modified = { ...mem };
      const entry = entries.find((e) => JSON.stringify(e[1]) === JSON.stringify(x));
      if (entry) {
        modified[entry[0]] = x;
      } else {
        console.log('bulkV4PropertiesAsAnObject prop not found:', x);
      }
      return modified;
    }, {});
  }

  static fromObject(obj, instance) {
    Object.keys(obj).forEach((key) => {
      instance[key] = obj[key];
    });
    return instance;
  }

  static getPatchPayload(entities: Validatable<any>[]) {
    let toUpdate;
    //
    // If the 'change' property is populated on any of the entities,
    // then use it to only send properties that were changed in the toUpdate object.
    // Else send all fields.
    //
    if (entities.some((x) => x.changes.length > 0)) {
      toUpdate = entities
        .filter((entity) => entity.id > -1)
        .reduce(
          (mem, x) => ({
            ...mem,
            [x.id]: x.toApi(),
          }),
          {},
        );
    } else {
      toUpdate = entities
        .filter((entity) => entity.id > -1)
        .reduce((mem, x) => {
          const modified = { ...mem };
          const apiModel = x.toApi();
          const updatableValues = this.bulkV4Properties.map((prop) => ({
            apiKey: prop.apiModelProperty,
            value: apiModel[prop.apiModelProperty],
          }));
          modified[x.id] = updatableValues.reduce(
            (mem2, y) => ({
              ...mem2,
              [y.apiKey]: y.value,
            }),
            {},
          );
          return modified;
        }, {});
    }

    return {
      showLoader: false,
      toAdd: entities.filter((x) => x.id === -1).map((x) => x.toApi()),
      toUpdate,
    };
  }

  static convertValueToEntityPropertyType(value, entityType) {
    switch (entityType) {
      case TableColumnTypes.integer:
      case TableColumnTypes.float:
        return isString(value) ? Number(value) : value;
      default:
        return value;
    }
  }

  static convertModelToEntityPropertyTypes(apiObject) {
    const convertedApiObject = apiObject;

    Object.entries(convertedApiObject).forEach(([key, value]) => {
      const matchingProperty = Object.values(this.entityProperties).find((prop) => prop.apiModelProperty === key);
      if (matchingProperty) {
        convertedApiObject[key] = this.convertValueToEntityPropertyType(value, matchingProperty.type);
      }
    });

    return convertedApiObject;
  }

  static async getEntityIDsFromModels(
    entities: Validatable<any>[],
  ): Promise<{ model: Validatable<any>; id: number }[]> {
    throw new Exception('Error: Did not implement `getEntityIDsFromModels` function');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async getEntities(ids: number[]): Promise<Validatable<any>[]> {
    throw new Exception('Error: Did not implement `getEntities` function');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async patchEntities(entities: Validatable<any>[]) {
    throw new Exception('Error: Did not implement `patchEntities` function');
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static async assignIDs(entities: Validatable<any>[]): Promise<Validatable<any>[]> {
    throw new Exception('Error: Did not implement `assignIDs` function');
  }

  getChangeFor(entityProperty: EntityProperty<T>) {
    if (!this.hasChangesFor(entityProperty)) return null;
    return this.changes.find((x) => x.property.property === entityProperty.property).change;
  }

  hasChangesFor(entityProperty: EntityProperty<T>) {
    return this.changes.some((x) => x.property.property === entityProperty.property);
  }

  isPristine(): boolean {
    throw new Exception('Error: Did not implement `isPristine` function');
  }

  toApi(): void {
    return null;
  }

  async getValidatableChanges<T>(
    originalObject: T,
    entityProperties: Record<string, EntityProperty<T>>,
    propExclusions: Array<string> = [],
  ): Promise<IEntityChange<T>[]> {
    GlobalEventBus.$emit(GlobalEvents.loading);

    try {
      const changes = [];
      await Promise.all(
        Object.entries(entityProperties)
          .filter(([entityPropertyKey]) => !propExclusions.includes(entityPropertyKey))
          .filter(([, prop]) => !prop.areValuesEqual(originalObject, this))
          .map(async ([, prop]) => {
            const isList = prop.type === 'list';

            const originalObjectString = await prop.toString(originalObject);
            const updatedObjectString = await prop.toString(this as unknown as T);
            const diff = isList ? await prop.getDiff(originalObject, this as unknown as T) : [];

            if (originalObjectString !== updatedObjectString || diff?.length) {
              changes.push(<IEntityChange<T>>{
                property: prop,
                updatedObject: this as unknown as T,
                change: {
                  from: isList ? null : originalObjectString,
                  to: isList ? null : updatedObjectString,
                  diff: isList ? diff : null,
                },
              });
            }
          }),
      );

      return changes.sort((a, b) => {
        const labelA = typeof a.property.label === 'function' ? a.property.label() : a.property.label;
        const labelB = typeof b.property.label === 'function' ? b.property.label() : b.property.label;
        return labelA.localeCompare(labelB);
      });
    } finally {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
  }
}

export default Validatable;
